"use client";

import React from "react";
import { ArrowRight, ChartBar, ClipboardCheck, FileSpreadsheet, ChartLine, Share2, Workflow } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const reportingSupports = [{
  title: "Financial Reporting Tools",
  description: "Build clearer reporting packages, budget summaries, variance tracking, and year-end support tools so leadership can understand financial information more easily.",
  icon: FileSpreadsheet
}, {
  title: "Program and Funding Dashboards",
  description: "Turn program budgets, funding requirements, deadlines, and reporting activity into practical dashboards that help teams stay informed.",
  icon: ChartBar
}, {
  title: "Workflow and Task Tracking",
  description: "Create practical systems for work plans, follow-up items, reporting deadlines, document requests, and project status updates.",
  icon: Workflow
}, {
  title: "Client Communication Systems",
  description: "Use SharePoint, Microsoft 365, Power BI, Excel, and related tools to help RiverBend and clients share information in a timely and organized way.",
  icon: Share2
}];
const exampleRows = [{
  label: "Program Budget",
  budget: "$250,000",
  actual: "$218,400",
  status: "On Track"
}, {
  label: "Staffing",
  budget: "$110,000",
  actual: "$104,800",
  status: "On Track"
}, {
  label: "Travel",
  budget: "$35,000",
  actual: "$29,600",
  status: "Monitor"
}, {
  label: "Supplies",
  budget: "$18,000",
  actual: "$16,750",
  status: "On Track"
}];
export default function VisualizingSuccess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="9730" id="customized-system-solutions" ref={ref} className="bg-primary-500 text-primary-foreground relative w-full overflow-hidden py-16 md:py-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.25),transparent_40%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_42%)] pointer-events-none" />

      <div className={cn("max-w-7xl mx-auto px-6 lg:px-8 relative z-10 transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="text-secondary-100 font-default font-bold tracking-widest uppercase text-sm mb-4">
              Reporting Systems & Automation
            </p>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight mb-6 text-white leading-tight">
              Turn complex information into practical reports people can use.
            </h2>

            <p className="text-primary-foreground/85 text-lg leading-relaxed font-default mb-8">
              RiverBend helps clients organize financial and operational
              information into clearer reporting tools, dashboards, work plans,
              and communication systems. The goal is not technology for its own
              sake. The goal is better information, better follow-up, and better
              decisions.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <ClipboardCheck className="size-5 text-secondary-100 shrink-0 mt-0.5" />
                <p className="text-primary-foreground/85 font-default leading-relaxed">
                  Support regular reporting to leadership, funders, boards,
                  councils, staff, and clients.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <ChartLine className="size-5 text-secondary-100 shrink-0 mt-0.5" />
                <p className="text-primary-foreground/85 font-default leading-relaxed">
                  Build tools that help teams track budget versus actual results,
                  deadlines, progress, and outstanding items.
                </p>
              </div>
            </div>

            <Button asChild size="lg" variant="secondary" className="h-12 px-8 rounded-md font-default">
              <Link to="/ContactPage">
                Talk to RiverBend
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] shadow-2xl overflow-hidden">
              <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-white font-default font-bold">
                    Program Reporting Snapshot
                  </p>
                  <p className="text-primary-foreground/65 text-sm font-default">
                    Example budget versus actual reporting view
                  </p>
                </div>

                <div className="hidden sm:flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-lg bg-secondary-100" />
                  <span className="h-2.5 w-2.5 rounded-lg bg-white/30" />
                  <span className="h-2.5 w-2.5 rounded-lg bg-white/20" />
                </div>
              </div>

              <div className="p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="rounded-lg bg-white/[0.06] border border-white/10 p-5">
                    <p className="text-primary-foreground/65 text-sm font-default mb-2">
                      Total Budget
                    </p>
                    <p className="text-2xl font-default font-bold text-white">
                      $250,000
                    </p>
                  </div>

                  <div className="rounded-lg bg-white/[0.06] border border-white/10 p-5">
                    <p className="text-primary-foreground/65 text-sm font-default mb-2">
                      Actual to Date
                    </p>
                    <p className="text-2xl font-default font-bold text-white">
                      $218,400
                    </p>
                  </div>

                  <div className="rounded-lg bg-secondary/20 border border-secondary/30 p-5">
                    <p className="text-primary-foreground/75 text-sm font-default mb-2">
                      Status
                    </p>
                    <p className="text-2xl font-default font-bold text-white">
                      On Track
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-black/10 overflow-hidden mb-6">
                  <div className="grid grid-cols-4 gap-4 px-5 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-primary-foreground/60 font-default">
                    <span>Category</span>
                    <span className="text-right">Budget</span>
                    <span className="text-right">Actual</span>
                    <span className="text-right">Status</span>
                  </div>

                  {exampleRows.map((row, index) => <div key={row.label} data-index={index} className="grid grid-cols-4 gap-4 px-5 py-4 border-b border-white/5 last:border-b-0 text-sm font-default">
                      <span className="text-white font-medium">{row.label}</span>
                      <span className="text-primary-foreground/75 text-right">
                        {row.budget}
                      </span>
                      <span className="text-primary-foreground/75 text-right">
                        {row.actual}
                      </span>
                      <span className="text-secondary-100 text-right font-semibold">
                        {row.status}
                      </span>
                    </div>)}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-lg bg-white/[0.06] border border-white/10 p-5">
                    <p className="text-white font-default font-semibold mb-3">
                      Budget Use
                    </p>
                    <div className="h-3 rounded-lg bg-white/10 overflow-hidden">
                      <div className="h-full w-[87%] bg-secondary" />
                    </div>
                    <p className="text-primary-foreground/65 text-sm font-default mt-3">
                      87% of planned funding reflected in current reporting.
                    </p>
                  </div>

                  <div className="rounded-lg bg-white/[0.06] border border-white/10 p-5">
                    <p className="text-white font-default font-semibold mb-3">
                      Reporting Follow-Up
                    </p>
                    <div className="h-3 rounded-lg bg-white/10 overflow-hidden">
                      <div className="h-full w-[72%] bg-secondary" />
                    </div>
                    <p className="text-primary-foreground/65 text-sm font-default mt-3">
                      72% of outstanding reporting items reviewed or resolved.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8 mt-12">
          {reportingSupports.map((item, index) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={index} className="h-full bg-white/[0.04] border border-white/10 shadow-sm rounded-lg">
                <CardContent className="p-6">
                  <div className="size-11 rounded-md bg-white/10 flex items-center justify-center mb-5">
                    <Icon className="size-5 text-secondary-100" />
                  </div>

                  <h3 className="text-lg font-default font-bold text-white mb-3">
                    {item.title}
                  </h3>

                  <p className="text-primary-foreground/75 font-default leading-relaxed text-sm">
                    {item.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}